import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FAVORITES_STORAGE_KEY,
  MAX_FAVORITES,
  createFavoriteEntry,
  favoriteSignatureFor,
  normalizeFavorites,
} from '../utils/savedData';

export const useFavorites = () => {
  const [items, setItems] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  const loadFavorites = useCallback(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        setItems(normalizeFavorites(parsed));
      }
    } catch (e) {
      console.warn('Failed to load favorites from localStorage:', e);
    }
  }, []);

  const add = useCallback((entry) => {
    const cleaned = createFavoriteEntry(entry);
    if (!cleaned) return null;

    setItems((prev) => {
      const signature = favoriteSignatureFor(cleaned);
      const withoutDuplicate = prev.filter((item) => favoriteSignatureFor(item) !== signature);
      return [cleaned, ...withoutDuplicate].slice(0, MAX_FAVORITES);
    });
    return cleaned;
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const removeEntry = useCallback((entry) => {
    if (!entry) return;
    const signature = favoriteSignatureFor(entry);
    setItems((prev) => prev.filter((item) => favoriteSignatureFor(item) !== signature));
  }, []);

  const toggle = useCallback((entry) => {
    if (!entry) return null;
    const signature = favoriteSignatureFor(entry);
    const existing = items.find((item) => favoriteSignatureFor(item) === signature);
    if (existing) {
      removeEntry(entry);
      return null;
    }
    return add(entry);
  }, [add, items, removeEntry]);

  const clear = useCallback(() => {
    setItems([]);
  }, []);

  const isSaved = useCallback((entry) => {
    if (!entry) return false;
    const signature = favoriteSignatureFor(entry);
    return items.some((item) => favoriteSignatureFor(item) === signature);
  }, [items]);

  useEffect(() => {
    loadFavorites();
    setHydrated(true);
  }, [loadFavorites]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('Failed to save favorites to localStorage:', e);
    }
  }, [items, hydrated]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (e) => {
      if (e.key !== FAVORITES_STORAGE_KEY) return;
      try {
        if (e.newValue == null) {
          setItems([]);
          return;
        }
        const parsed = JSON.parse(e.newValue);
        if (Array.isArray(parsed)) {
          setItems(normalizeFavorites(parsed));
        }
      } catch (err) {
        console.warn('Failed to sync favorites across tabs:', err);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return useMemo(() => ({ items, add, remove, removeEntry, toggle, clear, isSaved }), [
    items,
    add,
    remove,
    removeEntry,
    toggle,
    clear,
    isSaved,
  ]);
};
